<script setup lang="ts">
import { mdiLanguagePython, mdiGithub } from '@mdi/js'
import type { GithubTreeEntry } from '~/composables/api/useGithubApi'

const props = defineProps<{
  modelValue: boolean
  repoFullName: string
  repoName: string
}>()

const emit = defineEmits(['update:modelValue', 'select'])

const api = useApi()
const loading = ref(true)
const pyFiles = ref<GithubTreeEntry[]>([])
const selectedFile = ref<string[] | null>(null)
const error = ref<string | null>(null)

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

watch(() => props.modelValue, async (open) => {
  if (!open) return
  loading.value = true
  error.value = null
  selectedFile.value = null
  try {
    const parts = props.repoFullName.split('/')
    const owner = parts[0] ?? ''
    const repo = parts[1] ?? ''
    const res = await api.getGithubRepoTree(owner, repo)
    pyFiles.value = res.files
  }
  catch {
    error.value = 'ファイル一覧の取得に失敗しました'
    pyFiles.value = []
  }
  finally {
    loading.value = false
  }
})

const onSelect = () => {
  const selected = Array.isArray(selectedFile.value) ? selectedFile.value[0] : selectedFile.value
  if (selected) {
    emit('select', {
      repoFullName: props.repoFullName,
      entryPoint: selected,
    })
  }
}

const onCancel = () => {
  visible.value = false
}
</script>

<template>
  <v-dialog
    v-model="visible"
    max-width="500"
    persistent
  >
    <v-card class="entry-point-card">
      <v-card-title class="entry-point-title">
        <v-icon :icon="mdiGithub" />
        {{ repoName }}
      </v-card-title>
      <v-card-subtitle class="entry-point-subtitle">
        エントリーポイントとなる .py ファイルを選択してください
      </v-card-subtitle>

      <v-card-text>
        <div
          v-if="loading"
          class="text-center pa-4"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
          <p class="mt-2">
            ファイルを読み込み中...
          </p>
        </div>

        <div
          v-else-if="error"
          class="text-center pa-4 text-error"
        >
          {{ error }}
        </div>

        <div
          v-else-if="pyFiles.length === 0"
          class="text-center pa-4 text-grey"
        >
          .py ファイルが見つかりません
        </div>

        <v-list
          v-else
          v-model:selected="selectedFile"
          select-strategy="single-leaf"
          density="compact"
          class="entry-point-list"
        >
          <v-list-item
            v-for="file in pyFiles"
            :key="file.path"
            :value="file.path"
          >
            <template #prepend="{ isActive }">
              <v-list-item-action start>
                <v-radio
                  :model-value="isActive"
                  color="primary"
                />
              </v-list-item-action>
            </template>
            <v-list-item-title>
              <v-icon
                :icon="mdiLanguagePython"
                size="small"
                class="mr-1"
              />{{ file.path }}
            </v-list-item-title>
            <template #append>
              <span
                v-if="file.size"
                class="text-caption text-grey"
              >{{ (file.size / 1024).toFixed(1) }}KB</span>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          @click="onCancel"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :disabled="!selectedFile || selectedFile.length === 0"
          @click="onSelect"
        >
          選択
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.entry-point-card {
  font-family: "Noto Sans JP", sans-serif;
}

.entry-point-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.entry-point-subtitle {
  font-size: 0.85rem;
  opacity: 0.8;
}

.entry-point-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
